import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { GoldButton, GhostButton } from "./Buttons";
import { Reveal } from "./Reveal";
import { PROTECTION } from "../data/content";

const { audit, guarantee, banner } = PROTECTION;

async function openProtectedPdf(path, label) {
    try {
        const res = await fetch(path, { method: "HEAD" });
        const type = (res.headers.get("content-type") || "").toLowerCase();
        if (res.ok && type.includes("pdf")) {
            window.open(path, "_blank", "noopener,noreferrer");
            return;
        }
    } catch {
        // fall through to the graceful notice
    }
    toast.info(`The full ${label} document is being finalized. Message us on WhatsApp or email and we'll send you the current copy right away.`);
}

const BTN_FIT = "w-full sm:w-auto whitespace-normal sm:whitespace-nowrap";

function BenefitList({ benefits }) {
    return (
        <ul className="space-y-3 mt-6">
            {benefits.map((b) => {
                const Icon = b.icon;
                return (
                    <li key={b.text} className="flex items-start gap-3">
                        <span className="h-8 w-8 shrink-0 rounded-full border border-[#C8A45D]/40 bg-[#C8A45D]/10 flex items-center justify-center">
                            <Icon className="h-3.5 w-3.5 text-[#C8A45D]" />
                        </span>
                        <span className="text-white/70 text-sm leading-relaxed pt-1.5">{b.text}</span>
                    </li>
                );
            })}
        </ul>
    );
}

function GuaranteeModal() {
    const m = PROTECTION.guaranteeModal;
    return (
        <Dialog>
            <DialogTrigger asChild>
                <GhostButton data-testid="protection-guarantee-explore" className={BTN_FIT}>{guarantee.explore}</GhostButton>
            </DialogTrigger>
            <DialogContent className="bg-[#1C2B45] border-[#C8A45D]/30 text-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="protection-modal">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl sm:text-3xl text-white text-left">{m.title}</DialogTitle>
                    <DialogDescription className="text-[#C8A45D] text-[12px] uppercase tracking-[0.2em] text-left pt-1">{m.subtitle}</DialogDescription>
                </DialogHeader>
                <ol className="space-y-4 mt-2">
                    {m.steps.map((s, i) => (
                        <li key={s.title} className="flex gap-4" data-testid={`protection-modal-step-${i}`}>
                            <span className="h-8 w-8 shrink-0 rounded-full border border-[#C8A45D]/50 text-[#C8A45D] font-display flex items-center justify-center">{i + 1}</span>
                            <div>
                                <p className="text-white font-medium">{s.title}</p>
                                <p className="text-white/65 text-sm leading-relaxed mt-1">{s.text}</p>
                            </div>
                        </li>
                    ))}
                </ol>
                <div className="mt-6 space-y-4 text-sm">
                    <div>
                        <p className="text-[#C8A45D] text-[11px] uppercase tracking-[0.18em]">{m.provideTitle}</p>
                        <p className="text-white/70 leading-relaxed mt-1">{m.provide}</p>
                    </div>
                    <div>
                        <p className="text-[#C8A45D] text-[11px] uppercase tracking-[0.18em]">{m.coveredTitle}</p>
                        <p className="text-white/70 leading-relaxed mt-1">{m.covered}</p>
                    </div>
                    <div>
                        <p className="text-[#C8A45D] text-[11px] uppercase tracking-[0.18em]">{m.exclusionsTitle}</p>
                        <ul className="mt-2 space-y-2">
                            {m.exclusions.map((x) => (
                                <li key={x} className="flex gap-2 text-white/65 leading-relaxed">
                                    <span className="text-[#C8A45D] mt-1">·</span> {x}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <p className="text-white/40 text-xs italic leading-relaxed">{m.note}</p>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function ProtectionPromise() {
    return (
        <section className="section-pad bg-[#172337] border-y border-white/10" data-testid="protection-section">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal className="text-center">
                    <p className="text-[#C8A45D] text-[12px] uppercase tracking-[0.2em]">{PROTECTION.eyebrow}</p>
                    <h2 className="font-display text-white text-3xl sm:text-5xl tracking-tight leading-tight mt-5 max-w-3xl mx-auto">
                        We Stand Behind <em className="italic text-gold-gradient">Every Tax Return</em> We Prepare.
                    </h2>
                    <p className="text-[#C8A45D]/90 font-display italic text-lg sm:text-xl mt-5">{PROTECTION.supportHeadline}</p>
                    <div className="gold-rule mx-auto mt-6" />
                    <div className="max-w-3xl mx-auto mt-6 space-y-3">
                        {PROTECTION.intro.map((p) => (
                            <p key={p.slice(0, 24)} className="text-white/65 leading-relaxed">{p}</p>
                        ))}
                    </div>
                </Reveal>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-14">
                    {/* Card 1 — Audit Defense Commitment */}
                    <Reveal>
                        <div className="h-full bg-[#1C2B45] border border-[#C8A45D]/25 rounded-2xl p-8 sm:p-10 shadow-lg hover:border-[#C8A45D]/60 transition-[border-color,box-shadow] duration-300" data-testid="protection-card-audit">
                            <div className="flex items-center gap-5">
                                <span className="h-14 w-14 shrink-0 rounded-full border border-[#C8A45D]/50 bg-[#C8A45D]/10 flex items-center justify-center">
                                    {React.createElement(audit.icon, { className: "h-6 w-6 text-[#C8A45D]" })}
                                </span>
                                <h3 className="font-display text-2xl sm:text-[1.7rem] leading-snug text-white">{audit.headline}</h3>
                            </div>
                            <p className="text-white/60 text-sm leading-relaxed mt-5">{audit.description}</p>
                            <BenefitList benefits={audit.benefits} />
                            <div className="flex flex-col sm:flex-row flex-wrap gap-3 mt-8">
                                <GoldButton asChild data-testid="protection-audit-explore" className={BTN_FIT}>
                                    <Link to={audit.exploreHref}>{audit.explore}</Link>
                                </GoldButton>
                                <GhostButton data-testid="protection-audit-pdf" className={BTN_FIT} onClick={() => openProtectedPdf(audit.pdfPath, "Audit Defense Commitment")}>
                                    {audit.pdfLabel}
                                </GhostButton>
                            </div>
                        </div>
                    </Reveal>

                    {/* Card 2 — Tax Filing Responsibility Guarantee */}
                    <Reveal delay={0.08}>
                        <div className="h-full bg-[#1C2B45] border border-[#C8A45D]/25 rounded-2xl p-8 sm:p-10 shadow-lg hover:border-[#C8A45D]/60 transition-[border-color,box-shadow] duration-300" data-testid="protection-card-guarantee">
                            <div className="flex items-center gap-5">
                                <span className="h-14 w-14 shrink-0 rounded-full border border-[#C8A45D]/50 bg-[#C8A45D]/10 flex items-center justify-center">
                                    {React.createElement(guarantee.icon, { className: "h-6 w-6 text-[#C8A45D]" })}
                                </span>
                                <h3 className="font-display text-2xl sm:text-[1.7rem] leading-snug text-white">{guarantee.headline}</h3>
                            </div>
                            <p className="text-white/60 text-sm leading-relaxed mt-5">{guarantee.description}</p>
                            <BenefitList benefits={guarantee.benefits} />
                            <div className="flex flex-col sm:flex-row flex-wrap gap-3 mt-8">
                                <GuaranteeModal />
                            </div>
                        </div>
                    </Reveal>
                </div>

                {/* Trust banner */}
                <Reveal delay={0.1}>
                    <div className="mt-12 rounded-2xl border border-[#C8A45D]/40 bg-gradient-to-r from-[#C8A45D]/15 via-[#C8A45D]/5 to-transparent p-8 sm:p-10 flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10" data-testid="protection-banner">
                        <div className="flex-1">
                            <h3 className="font-display text-white text-xl sm:text-2xl uppercase tracking-wide leading-snug">{banner.headline}</h3>
                            <p className="text-white/65 text-sm leading-relaxed mt-3 max-w-2xl">{banner.text}</p>
                        </div>
                        <div className="shrink-0">
                            <GoldButton asChild data-testid="protection-banner-cta" className={BTN_FIT}>
                                <Link to={banner.ctaHref}>{banner.cta}</Link>
                            </GoldButton>
                        </div>
                    </div>
                </Reveal>

                {/* FAQ */}
                <Reveal delay={0.05}>
                    <h3 className="font-display text-white text-2xl sm:text-3xl text-center mt-16">{PROTECTION.faqTitle}</h3>
                </Reveal>
                <div className="max-w-3xl mx-auto mt-8">
                    <Accordion type="single" collapsible className="border-t border-white/10">
                        {PROTECTION.faqs.map((f, i) => (
                            <AccordionItem key={i} value={`protection-faq-${i}`} className="border-b border-white/10" data-testid={`protection-faq-item-${i}`}>
                                <AccordionTrigger className="font-display text-lg text-white hover:text-[#C8A45D] hover:no-underline text-left py-5 px-0">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-white/65 leading-relaxed pb-5 px-0">{f.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </div>
        </section>
    );
}
